import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Loader2, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useEnvironment, EnvironmentName } from '@/contexts/EnvironmentContext';

export default function LoginPage() {
  const { login, isLoading, error, clearError, isAuthenticated } = useAuth();
  const { current, environments, switchEnvironment } = useEnvironment();
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);

  // Redirect if already authenticated (but not while checking session)
  if (isAuthenticated && !isLoading) {
    navigate('/users', { replace: true });
    return null;
  }

  // Inputs uncontrolled + lecture via FormData : si on contrôle les valeurs
  // avec useState('') initial, React écrase la valeur autofillée par le
  // password manager au 1er render → Chrome/Safari ne détectent pas le
  // match avec l'entrée sauvegardée et reproposent « Enregistrer ».
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearError();
    const data = new FormData(e.currentTarget);
    const email = String(data.get('username') ?? '');
    const password = String(data.get('password') ?? '');
    try {
      await login(email, password, current.apiUrl);
      navigate('/users', { replace: true });
    } catch {
      // Error is handled by context
    }
  };

  const envColorMap: Record<string, string> = {
    blue: 'ring-blue-300 bg-blue-50',
    amber: 'ring-amber-300 bg-amber-50',
    red: 'ring-red-300 bg-red-50',
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-indigo-600 text-white mb-4">
            <Shield className="w-6 h-6" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900">EasyObeya Admin</h1>
          <p className="text-sm text-gray-500 mt-1">Platform Administration</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          {/* Environment selector */}
          <div className="mb-6">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              Environment
            </label>
            <select
              value={current.name}
              onChange={(e) => switchEnvironment(e.target.value as EnvironmentName)}
              className={`w-full px-3 py-2 rounded-lg text-sm font-medium ring-1 cursor-pointer focus:outline-none ${envColorMap[current.color] || ''}`}
            >
              {environments.map((env) => (
                <option key={env.name} value={env.name}>
                  {env.label}
                </option>
              ))}
            </select>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                Username
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                autoComplete="username"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="current-password"
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2 px-4 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              Sign In
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
